import { CalendarDays, FileText } from "lucide-react";
import { useMemo } from "react";
import { useHasSepaRemittancesQuery } from "@/features/accounting/hooks/useAccountingQueries";
import { useCollections } from "@/features/yeati-loc/hooks/useCollections";
import { checkFeatureRequirement } from "@/shared/configs/routeRegistry.config";
import {
  ACCOUNTING_STOCK_PAGE_PATH, ACCOUNTING_SEPA_REMITTANCES_PAGE_PATH, YEATI_LOC_PATH, YEATI_LOC_RESERVATIONS_PATH,
} from "@/shared/configs/routes.config";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import { useUserData, useOrganizationData } from "@/shared/hooks/useSidebarData";
import { cn } from "@/shared/lib/utils";
import { Separator } from "@/shared/ui/Separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/shared/ui/Sidebar";
import { getNavigationItems } from "@/shared/utils/navigation.utils";
import BeneficiarySpaceLink from "./BeneficiarySpaceLink";
import ChangelogLink from "./ChangelogLink";
import { CurrentOffersEntry } from "./CurrentOffersEntry";
import FavoritesSection from "./FavoritesSection";
import Navigation from "./Navigation";
import SidebarLogo from "./SidebarLogo";
import SidebarOrganizationInfo from "./SidebarOrganizationInfo";
import UserBadge from "./UserBadge";
const VERSION = import.meta.env.VITE_APP_VERSION;

const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";

  const userData = useUserData();
  const orgData = useOrganizationData(userData.userOrganizationId, userData.organizationNameFromRedux);
  const features = useOrganizationFeatures();
  const { permissions } = useRepresentativePermissions();

  const planFeatures = features.plan?.features ?? {};
  const navigationConfig = getNavigationItems(planFeatures, permissions);
  const canReadAccounting = navigationConfig.navigation.some((item) => !item.locked && item.items?.some(
    (child) => child.url === ACCOUNTING_STOCK_PAGE_PATH && !child.locked,
  ));
  const { data: hasSepaRemittances } = useHasSepaRemittancesQuery(userData.userOrganizationId, canReadAccounting);
  const showChangelog = checkFeatureRequirement("service_changelog", planFeatures);

  // Injection dynamique : une entrée « Réservations <collection> » par
  // collection active sous le parent Catalogue. Plus pertinent qu'une
  // entrée statique unique pour un CSE qui distingue p. ex. la
  // médiathèque livres des locations mobil-homes. La requête n'est tirée
  // que si le plan porte le catalogue : sans la feature, l'API répond 403 -
  // chaque montage de la sidebar affichait une erreur en console.
  const hasCatalog = checkFeatureRequirement("service_catalog", planFeatures);
  const { data: collectionsData } = useCollections(hasCatalog);
  const navigation = useMemo(() => {
    const activeCollections = Array.isArray(collectionsData)
      ? collectionsData.filter((c) => c.active)
      : [];
    const withSepaRemittances = navigationConfig.navigation.map((item) => {
      if (!canReadAccounting || !hasSepaRemittances || !item.items) return item;
      const stockIndex = item.items.findIndex((child) => child.url === ACCOUNTING_STOCK_PAGE_PATH);
      if (stockIndex < 0) return item;
      const items = [...item.items];
      items.splice(stockIndex + 1, 0, { name: "Remises SEPA", url: ACCOUNTING_SEPA_REMITTANCES_PAGE_PATH, icon: FileText });
      return { ...item, items };
    });
    if (!hasCatalog || activeCollections.length === 0) return withSepaRemittances;

    return withSepaRemittances.map((item) => {
      // On cible le parent Catalogue par préfixe d'URL — plus robuste
      // qu'un match sur le label (qui peut être traduit / renommé).
      if (!item.items || !item.url.startsWith(YEATI_LOC_PATH)) return item;
      const dynamicReservations = activeCollections.map((col) => ({
        name: `Réservations ${col.name.toLowerCase()}`,
        url: `${YEATI_LOC_RESERVATIONS_PATH}?category=${encodeURIComponent(col.name)}`,
        icon: CalendarDays,
      }));
      // Insère juste après « Collections » — emplacement attendu pour
      // les entrées Réservations dans la sidebar Catalogue.
      const items = item.items ?? [];
      const afterIdx = items.findIndex((i) => i.name === "Collections");
      const insertAt = afterIdx >= 0 ? afterIdx + 1 : items.length;
      const newItems = [...items];
      newItems.splice(insertAt, 0, ...dynamicReservations);
      return { ...item, items: newItems };
    });
  }, [hasCatalog, collectionsData, navigationConfig.navigation, canReadAccounting, hasSepaRemittances]);

  return (
    <Sidebar
      collapsible="icon"
      variant="floating"
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarLogo
              isLoading={orgData.isLoading}
              logo={orgData.logo}
              collapsed={collapsed}
            />
          </SidebarMenuItem>
          {!collapsed && <Separator className="my-2" />}
          <SidebarMenuItem>
            <SidebarOrganizationInfo
              organizationName={orgData.organizationName}
              external_id={orgData.external_id}
              collapsed={collapsed}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <FavoritesSection />
        <Navigation projects={navigation} />
      </SidebarContent>
      <SidebarFooter className="relative">
        {/* Fondu de scrollabilité : signale qu'il reste du contenu scrollable
            au-dessus du footer fixe. Utilise from-sidebar (fond réel, thème-safe)
            et s'auto-masque quand rien ne le recouvre (contenu court). */}
        <div className="pointer-events-none absolute inset-x-0 -top-5 z-10 h-5 bg-gradient-to-t from-sidebar to-transparent" />
        <CurrentOffersEntry collapsed={collapsed && !isMobile} onBeforeOpen={() => { if (isMobile) setOpenMobile(false); }} />
        <BeneficiarySpaceLink collapsed={collapsed} />
        <UserBadge
          user={{
            firstName: userData.firstName || "Y",
            lastName: userData.lastName || "I",
            email: userData.email || "leyeati@yeati.co",
            avatarUrl: userData.avatarUrl,
            roleName: userData.roleName,
          }}
        />
        <div className={cn(
          "pt-2 text-xs text-gray-400",
          collapsed ? "flex flex-col items-center gap-1" : "flex items-center justify-between",
        )}
        >
          <span className="font-mono">
            {collapsed ? VERSION : `v${VERSION}`}
          </span>
          {showChangelog && <ChangelogLink collapsed={collapsed} />}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
